"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Package, Loader2, AlertCircle, Lock, ChevronRight } from "lucide-react";
import StatusBar from "@/components/StatusBar";

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userFocused, setUserFocused] = useState(false);
  const [passFocused, setPassFocused] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [hintEmail, setHintEmail] = useState("");

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      setHintEmail("admin@bolpack.local");
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Error al iniciar sesión");
        setLoading(false);
        return;
      }

      router.push("/");
      router.refresh();
    } catch (err) {
      setError("Error de red. Intenta nuevamente.");
      setLoading(false);
    }
  }

  function useHint() {
    setEmail(hintEmail);
  }

  function handleUserKey(e) {
    if (e.key === "Enter" && userFocused) {
      e.preventDefault();
      document.getElementById("pass-input")?.focus();
    }
  }

  return (
    <div className="aurora-deep aurora-noise min-h-screen w-full flex flex-col relative overflow-hidden">
      {/* Center logon box */}
      <div className="flex-1 flex items-center justify-center p-4 relative z-10">
        {/* Decorative floating orbs (Vista Aurora feel) */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-aero-blue/15 blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-aero-alert/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full bg-aero-purple/8 blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col items-center fade-up">
          {/* Brand mark */}
          <div className="mb-8 flex flex-col items-center">
            <div className="relative mb-5">
              {/* Glow rings */}
              <div className="absolute inset-0 rounded-2xl gradient-primary glow-primary scale-125 opacity-30 blur-xl" />
              <div className="relative w-20 h-20 rounded-2xl gradient-primary flex items-center justify-center bevel-strong"
                style={{
                  boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.4),
                    inset 0 -1px 0 rgba(0,0,0,0.3),
                    0 0 30px rgba(91, 169, 224, 0.6),
                    0 8px 20px rgba(0,0,0,0.4)
                  `
                }}
              >
                <Package className="w-11 h-11 text-white drop-shadow-md" strokeWidth={2.3} />
              </div>
            </div>
            <h1
              className="text-5xl font-bold tracking-tight text-glow-soft"
              style={{
                background: "linear-gradient(180deg, #ffffff 0%, #b8d4ee 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              BOLPACK
            </h1>
            <p className="text-[11px] text-aero-blue-bright mt-2 tracking-[0.35em] font-semibold">
              SISTEMA DE PRODUCCIÓN
            </p>
          </div>

          {/* Welcome prompt — Vista style */}
          <div className="glass-aero-strong rounded-xl p-7 w-[420px] max-w-full relative">
            {/* Top gloss */}
            <div className="absolute inset-x-0 top-0 h-1/2 rounded-t-xl bg-gradient-to-b from-white/8 to-transparent pointer-events-none" />

            <div className="relative">
              <div className="mb-5 text-center">
                <p className="text-aero-text text-base font-medium">
                  Iniciar sesión
                </p>
                <p className="text-aero-text-muted text-xs mt-0.5">
                  Ingresa tus credenciales para continuar
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Email */}
                <div className="relative">
                  <label
                    className={`absolute left-3 transition-all pointer-events-none ${
                      email || userFocused
                        ? "top-1.5 text-[10px] uppercase tracking-wider font-semibold text-aero-blue-bright"
                        : "top-1/2 -translate-y-1/2 text-sm text-aero-text-muted"
                    }`}
                  >
                    {email || userFocused ? "Correo" : "Correo electrónico"}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setUserFocused(true)}
                    onBlur={() => setUserFocused(false)}
                    onKeyDown={handleUserKey}
                    className="input-aero w-full pt-5 pb-1.5 px-3 rounded-md text-sm"
                    autoComplete="email"
                    autoFocus
                  />
                </div>

                {/* Password */}
                <div className="relative">
                  <label
                    className={`absolute left-3 transition-all pointer-events-none ${
                      password || passFocused
                        ? "top-1.5 text-[10px] uppercase tracking-wider font-semibold text-aero-blue-bright"
                        : "top-1/2 -translate-y-1/2 text-sm text-aero-text-muted"
                    }`}
                  >
                    {password || passFocused ? "Contraseña" : "Contraseña"}
                  </label>
                  <input
                    id="pass-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPassFocused(true)}
                    onBlur={() => setPassFocused(false)}
                    className="input-aero w-full pt-5 pb-1.5 px-3 rounded-md text-sm"
                    autoComplete="current-password"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-2.5 rounded-md bg-aero-alert/15 border border-aero-alert/40 text-aero-alert-bright text-xs fade-up">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-aero w-full py-2.5 rounded-md font-semibold text-sm flex items-center justify-center gap-2 mt-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Ingresando...
                    </>
                  ) : (
                    <>
                      Ingresar
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              {/* Hint in dev */}
              {process.env.NODE_ENV === "development" && hintEmail && (
                <div className="mt-5 pt-4 border-t border-white/10 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-aero-text-dim mb-1.5 font-semibold">
                    Cuenta de prueba
                  </p>
                  <button
                    type="button"
                    onClick={useHint}
                    className="text-xs font-mono text-aero-blue-bright hover:text-aero-text transition-colors"
                  >
                    {hintEmail}
                  </button>
                  <p className="text-[9px] text-aero-text-dim/60 mt-1">
                    contraseña en README.md
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Hint below box */}
          <div className="mt-6 flex items-center gap-2 text-[11px] text-aero-text-muted">
            <Lock className="w-3 h-3" />
            <span>Conexión segura · Sesión cifrada</span>
          </div>
        </div>
      </div>

      {/* Bottom status bar (Vista taskbar style) */}
      <StatusBar stats={{ departments: 6, users: 7 }} />
    </div>
  );
}
